import { z } from 'zod'

import { filterFor, serializeOrderStatuses, type OrderTabKey } from '@/features/group/order-tabs'
import type { GroupOrder } from '@/features/group/schema'
import type {
  OrderListFilters,
  OrderListStatusCounts,
  OrderListFilterGroup,
} from '@/features/order-list/schema'

export const orderManagementSearchSchema = z.object({
  tab: z.enum(['unpaid', 'billed', 'processing', 'shipped', 'done']).catch('unpaid'),
  search: z.string().catch(''),
  source: z.string().catch(''),
  group: z.string().catch(''),
})
export type OrderManagementSearch = z.infer<typeof orderManagementSearchSchema>
export const defaultOrderManagementSearch = orderManagementSearchSchema.parse({})

export function orderManagementFilters(search: OrderManagementSearch): OrderListFilters {
  return {
    orderStatus: serializeOrderStatuses(filterFor(search.tab).orderStatuses),
    search: search.search.trim() || undefined,
    sources: search.source || undefined,
    groups: search.group || undefined,
  }
}

export function listTabCounts(counts: OrderListStatusCounts): Record<OrderTabKey, number> {
  return {
    unpaid: counts.unpaid,
    billed: counts.billed,
    processing: counts.inProcess,
    shipped: counts.shipped,
    done: counts.completed,
  }
}

export function groupOrdersByOwner(orders: readonly GroupOrder[]) {
  const groups = new Map<string, GroupOrder[]>()
  for (const order of orders) {
    if (!order.groupId) continue
    const group = groups.get(order.groupId) ?? []
    group.push(order)
    groups.set(order.groupId, group)
  }
  return groups
}

export function canDeleteListedOrder(order: GroupOrder) {
  return order.status === 'UNPAID' && Boolean(order.groupId)
}

export function orderSourceLabel(source: string) {
  if (source === 'WHATSAPP') return 'Grup WhatsApp'
  if (source === 'MANUAL') return 'Manual'
  if (source === 'AI') return 'Rekap AI'
  return source || 'Sumber tidak tersedia'
}

export function orderGroupLabel(
  id: string,
  groups: readonly OrderListFilterGroup[],
  pending = false,
) {
  return (
    groups.find((group) => group.id === id)?.name?.trim() ||
    (pending ? 'Memuat nama grup…' : 'Grup tidak tersedia')
  )
}

export function activeOrderFilters(
  search: OrderManagementSearch,
  groups: readonly OrderListFilterGroup[],
  pending = false,
) {
  const filters: { key: 'source' | 'group' | 'search'; label: string }[] = []
  if (search.source)
    filters.push({ key: 'source', label: `Sumber: ${orderSourceLabel(search.source)}` })
  if (search.group)
    filters.push({ key: 'group', label: `Grup: ${orderGroupLabel(search.group, groups, pending)}` })
  if (search.search.trim()) filters.push({ key: 'search', label: `Cari: ${search.search.trim()}` })
  return filters
}
